import { InlineCode } from "@/once-ui/components";
import Link from "next/link";

const person = {
  firstName: "Thoriq",
  lastName: "Harizul",
  get name() {
    return `${this.firstName} ${this.lastName} Ahsan`;
  },
  role: "Software Engineer",
  avatar: "/images/avatar.jpg",
  location: "Asia/Jakarta",
  languages: ["English", "Indonesian"],
};

const newsletter = {
  display: true,
  title: <>Build & Scale</>,
  description: (
    <>
      Practical insights on building end-to-end applications, from crafting
      mobile experiences with Flutter to scaling backend systems.
    </>
  ),
};

const social = [
  {
    name: "LinkedIn",
    icon: "linkedin",
    link: "https://www.linkedin.com/in/thoriq-harizul-ahsan/",
  },
  {
    name: "GitHub",
    icon: "github",
    link: "https://github.com/Thoriq-ha",
  },
  {
    name: "Email",
    icon: "email",
    link: "mailto:ithoriq0@gmail.com",
  },
];

const home = {
  label: "Home",
  title: `${person.firstName}'s Portfolio`,
  description: `Portfolio website showcasing my work as a ${person.role}`,
  headline: <>Software Engineer</>,
  subline: (
    <>
      I build and scale full-stack applications, bridging the gap between
      innovative products and reliable infrastructure.
    </>
  ),
};

const about = {
  label: "About",
  title: "About me",
  description: `Meet ${person.name}, a versatile Software & DevOps Engineer from Indonesia.`,
  tableOfContent: {
    display: true,
    subItems: true,
  },
  avatar: {
    display: true,
  },
  calendar: {
    display: false,
    link: "",
  },
  intro: {
    display: true,
    title: "Introduction",
    description: (
      <>
        I am a Software Engineer with a full-stack skillset, passionate about
        building high-quality applications and the robust infrastructure that
        supports them.
        <br />
        <br />
        With experience leading product development from scratch at{" "}
        <InlineCode
          style={{ padding: "var(--static-space-2) var(--static-space-8)" }}
        >
          <Link href="#tarkam">Tarkam.co.id</Link>
        </InlineCode>
        , implementing CI/CD pipelines at a national media company like{" "}
        <InlineCode
          style={{ padding: "var(--static-space-2) var(--static-space-8)" }}
        >
          <Link href="#kompastv">Kompas TV</Link>
        </InlineCode>
        , and validated by certifications from{" "}
        <InlineCode
          style={{ padding: "var(--static-space-2) var(--static-space-8)" }}
        >
          Google Cloud & Alibaba Cloud
        </InlineCode>
        , I have the ability to bridge the gap between software development and
        operations to achieve maximum efficiency.
      </>
    ),
  },
  work: {
    display: true,
    title: "Experience",
    experiences: [
      {
        company: "Kompas TV",
        timeframe: "October 2023 - Present",
        role: "System Developer",
        id: "kompastv",
        achievements: [
          <>
            Initiated and implemented a <strong>Self-Hosted GitLab</strong>,
            building a CI/CD pipeline for a team of 15+ developers and
            increasing deployment frequency by 50%.
          </>,
          <>
            Led the development of a large-scale{" "}
            <strong>Media Asset Management</strong> system using .NET and
            Docker.
          </>,
        ],
        images: [],
      },
      {
        company: "Tarkam.co.id",
        timeframe: "August 2023 - Present",
        role: "Co-founder & Head of Product",
        id: "tarkam",
        achievements: [
          <>
            Led the product development of a mobile app (Flutter) from idea to
            achieving <strong>500+ downloads</strong> on the Play Store.
          </>,
          <>
            Implemented a full CI/CD pipeline and secured the production
            infrastructure using{" "}
            <strong>Cloudflare (WAF & DDoS Protection)</strong>.
          </>,
          <>
            Integrated AI technology for an automated player detection feature.
          </>,
        ],
        images: [],
      },
      {
        company: "Freelance",
        timeframe: "June 2024 - Present",
        role: "System Administrator",
        achievements: [
          <>
            Managed and maintained several Linux servers for news web platforms,
            ensuring <strong>99.9% uptime</strong>.
          </>,
        ],
        images: [],
      },
    ],
  },
  certifications: {
    display: false,
    title: "Certifications",
    items: [
      {
        name: "Build Infrastructure with Terraform on Google Cloud",
        issuer: "Google",
        date: "Sep 2024",
      },
      {
        name: "Manage Kubernetes in Google Cloud",
        issuer: "Google",
        date: "Sep 2024",
      },
      {
        name: "Create and Manage Cloud SQL for PostgreSQL Instances",
        issuer: "Google",
        date: "Sep 2024",
      },
      {
        name: "Alibaba Cloud Certified Developers",
        issuer: "Alibaba Cloud",
        date: "Aug 2024",
      },
    ],
  },
  studies: {
    display: true,
    title: "Education",
    institutions: [
      {
        name: "UIN Malang",
        description: (
          <>Bachelor of Informatics Engineering (S.Kom.) - GPA 3.74 / 4.0</>
        ),
      },
    ],
  },
  technical: {
    display: true,
    title: "Technical Skills",
    skills: [
      {
        title: "Mobile & Frontend",
        description: (
          <>
            Experienced in building high-performance, cross-platform mobile
            applications with <strong>Flutter & Dart</strong>. Possess web
            frontend development skills using React.js.
          </>
        ),
        images: [],
      },
      {
        title: "Backend Development",
        description: (
          <>
            Proficient in server-side development using various technologies
            like <strong>C# (.NET), PHP (Laravel), Go,</strong> and{" "}
            <strong>JavaScript (Node.js)</strong> to build robust and scalable
            APIs.
          </>
        ),
        images: [],
      },
      {
        title: "Cloud & DevOps",
        description: (
          <>
            Deep expertise in managing cloud infrastructure (
            <strong>GCP, AWS</strong>), container orchestration with{" "}
            <strong>Kubernetes & Docker</strong>, and Infrastructure as Code
            using <strong>Terraform</strong>. Experienced in building{" "}
            <strong>CI/CD</strong> pipelines with GitLab.
          </>
        ),
        images: [],
      },
      {
        title: "Databases & Tools",
        description: (
          <>
            Skilled in managing relational databases like{" "}
            <strong>PostgreSQL, MySQL, and SQL Server</strong>. Highly
            proficient with Git for version control.
          </>
        ),
        images: [],
      },
    ],
  },
};

const blog = {
  label: "Blog",
  title: "Writings on AI, Sofware Dev, and Tech",
  description: `Explore deep dives into full-stack development, DevOps culture, and the lessons learned while building and scaling software products.`,
  // Create new blog posts by adding a new .mdx file to app/blog/posts
  // All posts will be listed on the /blog route
};

const work = {
  label: "Work",
  title: "My projects",
  description: `Design and dev projects by ${person.name}`,
  // Create new project pages by adding a new .mdx file to app/blog/posts
  // All projects will be listed on the /home and /work routes
};

const store = {
  label: "Store",
};

const gallery = {
  label: "Gallery",
  title: "My photo gallery",
  description: `A photo collection by ${person.name}`,
  images: [
    {
      src: "/images/gallery/img-01.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "56mm | 1/1250 | f11 | ISO100",
      location: "Zillertal, Austria",
    },
    {
      src: "/images/gallery/img-02.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "59mm | 1/100 | f11 | ISO100",
      location: "Učka, Croatia",
    },
    {
      src: "/images/gallery/img-03.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/20 | f22 | ISO100",
      location: "Falasarna Beach, Crete",
    },
    {
      src: "/images/gallery/img-04.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "27mm | 1/250 | f9.0 | ISO100",
      location: "Sao Lourenco, Madeira",
    },
    {
      src: "/images/gallery/img-06.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/250 | f9.0 | ISO100",
      location: "Guimaraes, Portugal",
    },
    {
      src: "/images/gallery/img-42.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/125 | f3.5 | ISO200",
      location: "Paiva Walkways, Portugal",
    },
    {
      src: "/images/gallery/img-07.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/50 | f7.1 | ISO1600",
      location: "Kissamos, Croatia",
    },
    {
      src: "/images/gallery/img-52.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "45mm | 1/60 | f7.1 | ISO320",
      location: "Seoul, South Korea",
    },
    {
      src: "/images/gallery/img-09.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/160 | f11 | ISO100",
      location: "Seitan Limania, Crete",
    },
    {
      src: "/images/gallery/img-22.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "135mm | 1/30 | f11 | ISO250",
      location: "Seoul, South Korea",
    },
    {
      src: "/images/gallery/img-35.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/80 | f5.6 | ISO200",
      location: "Peneda-Gerês, Portugal",
    },
    {
      src: "/images/gallery/img-24.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "51mm | 1/400 | f11 | ISO100",
      location: "Tatra Mountains, Poland",
    },
    {
      src: "/images/gallery/img-26.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/100 | f7.1 | ISO100",
      location: "Zakopane, Poland",
    },
    {
      src: "/images/gallery/img-30.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "28mm | 1/500 | f5.6 | ISO100",
      location: "Großglockner, Austria",
    },
    {
      src: "/images/gallery/img-31.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "33mm | 1/640 | f4.5 | ISO125",
      location: "Schladming, Austria",
    },
    {
      src: "/images/gallery/img-33.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/1250 | f9.0 | ISO125",
      location: "Dachstein, Austria",
    },
    {
      src: "/images/gallery/img-57.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "22mm | 1/500 | f4.0 | ISO100",
      location: "Seoul, South Korea",
    },
    {
      src: "/images/gallery/img-34.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "51mm | 1/200 | f5.0 | ISO200",
      location: "Peneda-Gerês, Portugal",
    },
    {
      src: "/images/gallery/img-45.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 25.0 | f3.5 | ISO800",
      location: "Crete, Greece",
    },
    {
      src: "/images/gallery/img-53.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Seoul, South Korea",
    },
    {
      src: "/images/gallery/img-58.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "67mm | 1/80 | f7.1 | ISO250",
      location: "Levada Nova, Madeira",
    },
    {
      src: "/images/gallery/img-59.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "28mm | 1/8 | f22 | ISO100",
      location: "Budapest, Hungary",
    },
    {
      src: "/images/gallery/img-60.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 13.0 | f11 | ISO100",
      location: "Dubai, UAE",
    },
    {
      src: "/images/gallery/img-74.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "75mm | 1/80 | f6.3 | ISO200",
      location: "Bled, Slovenia",
    },
    {
      src: "/images/gallery/img-61.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/400 | f13 | ISO100",
      location: "Dubai, UAE",
    },
    {
      src: "/images/gallery/img-62.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/125 | f11 | ISO320",
      location: "Morskie Oko, Poland",
    },
    {
      src: "/images/gallery/img-66.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/25 | f11 | ISO100",
      location: "Sardinia, Italy",
    },
    {
      src: "/images/gallery/img-67.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "92mm | 1/80 | f7.1 | ISO200",
      location: "Amsterdam, Netherlands",
    },
    {
      src: "/images/gallery/img-68.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "27mm | 1/3 | f25 | ISO100",
      location: "Sao Miguel, Azores",
    },
    {
      src: "/images/gallery/img-69.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/250 | f11 | ISO125",
      location: "Fanal Forest, Madeira",
    },
    {
      src: "/images/gallery/img-63.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "27mm | 1/200 | f4.5 | ISO250",
      location: "Zakopane, Poland",
    },
    {
      src: "/images/gallery/img-75.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "32mm | 1/50 | f9.0 | ISO320",
      location: "Bohinj, Slovenia",
    },
    {
      src: "/images/gallery/img-85.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/50 | f5.6 | ISO640",
      location: "Budapest, Hungary",
    },
    {
      src: "/images/gallery/img-84.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "50mm | 1/50 | f5.6 | ISO200",
      location: "Budapest, Hungary",
    },
    {
      src: "/images/gallery/img-72.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "18mm | 1/40 | f22 | ISO100",
      location: "Sao Miguel, Azores",
    },
    {
      src: "/images/gallery/img-73.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "63mm | 1/40 | f6.3 | ISO500",
      location: "Bohinj, Slovenia",
    },
    {
      src: "/images/gallery/img-76.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Madeira, Portugal",
    },
    {
      src: "/images/gallery/img-64.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Zakopane, Poland",
    },
    {
      src: "/images/gallery/img-70.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Azores, Portugal",
    },
    {
      src: "/images/gallery/img-77.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Zell am See, Austria",
    },
    {
      src: "/images/gallery/img-78.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Madeira, Portugal",
    },
    {
      src: "/images/gallery/img-79.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Bled, Slovenia",
    },
    {
      src: "/images/gallery/img-80.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Madeira, Portugal",
    },
    {
      src: "/images/gallery/img-81.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Cyprus, Greece",
    },
    {
      src: "/images/gallery/img-65.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Zakopane, Poland",
    },
    {
      src: "/images/gallery/img-82.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Halstatt, Austria",
    },
    {
      src: "/images/gallery/img-71.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Azores, Portugal",
    },
    {
      src: "/images/gallery/img-83.jpg",
      alt: "image",
      orientation: "vertical",
      technical: "91mm | 1/15 | f5.6 | ISO200",
      location: "Budapest, Hungary",
    },
  ],
};

export { person, social, newsletter, home, about, blog, work, gallery, store };
